import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useGoHomeShortcut from '../../hooks/useGoHomeShortcut';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { playNotificationSound, requestNotificationPermission, showBrowserNotification, vibrate } from '../../utils/notifications';
import { PWAInstallBanner } from '../pwa/PWAComponents';
import { RoleBadge, ROLE_LABELS } from './RoleBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Truck, LayoutDashboard, Database, MessageSquareText, Bell, Settings, LogOut,
  Menu, X, ChevronDown, User, MapPin, Sun, Moon, Calculator, Clock, FlaskConical, Eye, FileQuestion, Store, Link2, Upload, Shield, Route, Ship, Building2, Brain, ClipboardCheck, MoreHorizontal, Car, AlertTriangle, Download, Wrench, Gavel, Warehouse as WarehouseIcon
} from 'lucide-react';
import LanguageSelector from '../LanguageSelector';
import ClockWidget from '../ClockWidget';
import RenewalReminder from '../RenewalReminder';
import { BetaDisclaimerModal, BugReportButton, BetaBadge } from '../BetaDisclaimer';
import { OverdueBanner } from '../OverdueBanner';
import { PushNotificationBanner } from '../PushNotificationBanner';
import { SUBSCRIPTION_TIERS } from '../../context/SubscriptionContext';
import AIAssistant from '../AIAssistant';
import { useWebSocket } from '../../hooks/useWebSocket';
import { CompactTablesToggle } from './CompactTablesToggle';
import { MyTagsList } from './MyTagsList';
import { OnlineUsersIndicator } from './OnlineUsersIndicator';
import MovingOnboardingWizard from '../moving/MovingOnboardingWizard';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

export const MainLayout = ({ children }) => {
  const {
    user, logout, api, isSuperAdmin, testMode, setTestAsRole, isInTestMode, exitTestMode, actualUser,
    superAdminActingAs, superAdminActingAsName, setSuperAdminActingAs,
  } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { hasFeature, isStandalone, getSubscriptionType, getStandaloneProductName, loading: subscriptionLoading, testTier, setTestSubscriptionTier, isInTierTestMode } = useSubscription();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // iter 291: auto-close the mobile sidebar whenever the route changes
  // (including browser back/forward). Without this the drawer stays open on
  // top of the new page and covers content.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);
  const [notifications, setNotifications] = useState([]);
  const [showMyTags, setShowMyTags] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // iter 307.9 — Moving-closing bell pulse. Fires for 10s whenever a
  // WS `type: 'notification'` with `notification_type` starting
  // `moving_closing` lands, so the field crew sees the bell flash in
  // the top-nav even if the Sonner toast is dismissed.
  const [bellPulse, setBellPulse] = useState(false);
  const bellPulseTimerRef = React.useRef(null);
  const [companyData, setCompanyData] = useState(null);
  const [isOwnerOperator, setIsOwnerOperator] = useState(false);
  const [complianceAlerts, setComplianceAlerts] = useState({ overdue: 0, due_soon: 0, has_critical: false });
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // Fetch compliance alerts for badge (Admin only)
  useEffect(() => {
    const fetchComplianceAlerts = async () => {
      // Only fetch for admin and super_admin (compliance is admin-only module)
      if (!user || !['admin', 'super_admin'].includes(user.role)) return;
      try {
        const response = await api.get('/compliance/alerts/summary');
        setComplianceAlerts(response.data || { overdue: 0, due_soon: 0, has_critical: false });
      } catch (error) {
        console.debug('Failed to fetch compliance alerts:', error);
      }
    };
    
    fetchComplianceAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchComplianceAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [api, user]);

  // Fetch unread message count for badge
  useEffect(() => {
    const fetchUnreadMessages = async () => {
      if (!user || !['admin', 'super_admin', 'dispatcher', 'accounting'].includes(user.role)) return;
      try {
        const response = await api.get('/messages/unread-count');
        setUnreadMessageCount(response.data?.unread_count || 0);
      } catch (error) {
        console.debug('Failed to fetch unread messages:', error);
      }
    };
    
    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 30 * 1000);
    return () => clearInterval(interval);
  }, [api, user]);

  // Check if user is registered as owner operator
  useEffect(() => {
    const checkOwnerOperator = async () => {
      if (!user) return;
      
      // Super admin always has access to Owner Operator
      if (user.role === 'super_admin') {
        setIsOwnerOperator(true);
        return;
      }
      
      try {
        const response = await api.get('/owner-op/profile');
        // If we get a profile, user is an owner operator
        setIsOwnerOperator(!!response.data);
      } catch (error) {
        // Not an owner operator or error - that's fine
        setIsOwnerOperator(false);
      }
    };
    
    // Check for drivers, admins, and super_admin
    if (user && (user.role === 'driver' || user.role === 'admin' || user.role === 'super_admin')) {
      checkOwnerOperator();
    }
  }, [api, user]);

  // Fetch company data for logo
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await api.get('/company');
        if (response.data) {
          setCompanyData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch company:', error);
      }
    };
    
    // Fetch company only for non-super-admin users
    if (user && user.role !== 'super_admin') {
      fetchCompany();
    }
  }, [api, user]);

  // Load user language preference
  useEffect(() => {
    const loadLanguage = async () => {
      try {
        const response = await api.get('/user/preferences');
        if (response.data?.language && response.data.language !== i18n.language) {
          i18n.changeLanguage(response.data.language);
        }
      } catch (error) {
        console.error('Failed to load language preference');
      }
    };
    loadLanguage();
  }, [api, i18n]);

  // Save language preference when changed
  const handleLanguageChange = async (langCode) => {
    try {
      await api.put('/user/preferences', { language: langCode });
    } catch (error) {
      console.error('Failed to save language preference');
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifResponse, countResponse] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      setNotifications(notifResponse.data);
      setUnreadCount(countResponse.data.count);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  }, [api]);

  // Handle real-time WebSocket notifications for messages.
  // Subscribes to the singleton WebSocketProvider — does NOT open its own
  // connection. This is what was previously causing 8x duplicate fires.
  const handleLayoutWsMessage = useCallback((data) => {
    // Handle new chat message - refresh badge + show notification
    if (data.type === 'new_message') {
      const msg = data.message;
      const isCatchUp = data.is_catch_up;

      api.get('/messages/unread-count').then(res => {
        setUnreadMessageCount(res.data?.unread_count || 0);
      }).catch(() => {});

      // Show sound + toast for incoming messages (dedup by msg ID)
      if (msg && msg.sender_id !== user?.user_id && !isCatchUp) {
        const msgId = msg.id;
        if (msgId && !window._draylyNotified?.has(msgId)) {
          if (!window._draylyNotified) window._draylyNotified = new Set();
          window._draylyNotified.add(msgId);
          setTimeout(() => window._draylyNotified?.delete(msgId), 5000);

          playNotificationSound();
          vibrate([200, 100, 200]);

          const senderName = data.sender?.name || 'New message';
          const preview = msg.content?.substring(0, 80) || (msg.file_url ? 'Sent a file' : 'New message');

          toast.info(senderName, {
            description: preview,
            duration: 4000,
            id: `msg-${msgId}`
          });

          // Show native notification via service worker if available
          if ('Notification' in window && Notification.permission === 'granted') {
            const tag = `drayly-msg-${msgId}`;
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
              navigator.serviceWorker.ready.then(reg => {
                reg.showNotification(senderName, {
                  body: preview,
                  icon: '/logo192.png',
                  badge: '/logo192.png',
                  tag,
                  data: { type: 'message', conversation_id: msg.conversation_id }
                });
              }).catch(() => {});
            }
          }
        }
      }
    }

    // Handle messages read - refresh unread count
    if (data.type === 'messages_read') {
      api.get('/messages/unread-count').then(res => {
        setUnreadMessageCount(res.data?.unread_count || 0);
      }).catch(() => {});
    }

    // Handle notification messages - support both formats
    if (data.type === 'notification') {
      const notif = data.notification || data.data;

      if (!notif) return;

      // iter 307.9 — Flash the top-nav bell when a moving-closing
      // notification lands (admin-created-on-your-behalf, etc.). Clears
      // automatically after 10s or when the bell is clicked.
      if ((notif.type || '').startsWith('moving_closing')) {
        setBellPulse(true);
        if (bellPulseTimerRef.current) clearTimeout(bellPulseTimerRef.current);
        bellPulseTimerRef.current = setTimeout(() => setBellPulse(false), 10000);
      }

      if (notif.id) {
        setNotifications(prev => {
          const exists = prev.some(n => n.id === notif.id);
          if (exists) return prev;
          return [{ ...notif, read: notif.read === true ? true : false }, ...prev.slice(0, 49)];
        });
      }

      fetchNotifications();

      if (Notification.permission === 'granted') {
        new Notification(notif.title || 'Drayly Alert', {
          body: notif.message,
          icon: '/logo192.png',
          tag: notif.id || notif.item_id || Date.now().toString(),
          requireInteraction: notif.priority === 'urgent'
        });
      }

      if (notif.priority === 'urgent' || notif.priority === 'high') {
        if (notif.priority === 'urgent') {
          toast.error(notif.title, {
            description: notif.message,
            duration: 10000
          });
        } else {
          toast.warning(notif.title, {
            description: notif.message,
            duration: 5000
          });
        }
      }
    }
  }, [api, user?.user_id, fetchNotifications]);

  useWebSocket(handleLayoutWsMessage);

  // Request notification permission and subscribe to push on mount
  useEffect(() => {
    if (!user?.user_id) return;
    
    const setupPush = async () => {
      if (!('PushManager' in window) || !('serviceWorker' in navigator)) return;
      
      if (Notification.permission === 'granted') {
        try {
          const reg = await navigator.serviceWorker.ready;
          let subscription = await reg.pushManager.getSubscription();
          
          if (!subscription) {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            let vapidKey;
            try {
              const res = await fetch(`${API_URL}/api/notifications/vapid-public-key`);
              if (res.ok) {
                const data = await res.json();
                vapidKey = data.publicKey;
              }
            } catch {
              // VAPID key fetch failed — leave undefined, guarded below
            }
            
            if (!vapidKey) return; // Can't subscribe without VAPID key
            
            const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
            const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
            const rawData = window.atob(base64);
            const outputArray = new Uint8Array(rawData.length);
            for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
            
            subscription = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: outputArray
            });
            console.log('[Push] New push subscription created');
          }
          
          // Always re-register with backend to ensure it's active
          if (subscription) {
            // iter 264 — migrated off direct localStorage read; use the
            // AuthContext axios instance so the request goes through the
            // silent-refresh interceptor.
            try {
              await api.post('/notifications/subscribe', {
                subscription: subscription.toJSON(),
                app_type: 'dispatch',
              });
              console.log('[Push] Dispatch push subscription registered with backend');
            } catch (err) {
              console.error('[Push] Failed to register push subscription:', err);
            }
          }
        } catch (e) {
          console.error('[Push] Failed to setup push subscription:', e);
        }
      }
      // Permission is only requested from an explicit "Enable" tap (banner / driver prompt);
      // hijacking the first random tap made phones auto-block the site.
    };
    
    // Delay to let service worker activate first
    const timer = setTimeout(setupPush, 3000);
    return () => clearTimeout(timer);
  }, [user?.user_id, api]);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds (backup for WebSocket)
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      // Optimistic update - mark all as read locally
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      
      // Then sync with server
      await api.put('/notifications/mark-all-read');
      
      // Refresh to ensure sync
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read');
      // Refresh to reset state on error
      fetchNotifications();
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      await api.delete('/notifications/clear-all');
      setNotifications([]);
      setUnreadCount(0);
      // Use dynamic import for toast
      import('sonner').then(({ toast }) => {
        toast.success('All notifications cleared');
      });
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      import('sonner').then(({ toast }) => {
        toast.error('Failed to clear notifications');
      });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Build nav items based on subscription type
  const getNavItems = () => {
    const subscriptionType = getSubscriptionType();
    
    // For standalone TimeClock subscription
    if (subscriptionType === 'standalone_timeclock') {
      return [
        { path: '/timeclock', labelKey: 'nav.timeclockPro', icon: Clock },
        ...(user?.role === 'admin' || user?.role === 'super_admin' ? [{ path: '/users', labelKey: 'nav.users', icon: User }] : [])
      ];
    }
    
    // For standalone PayTrack subscription
    if (subscriptionType === 'standalone_accounting') {
      return [
        { path: '/paytrack', labelKey: 'nav.paytrack', icon: Calculator },
        ...(user?.role === 'admin' || user?.role === 'super_admin' ? [{ path: '/users', labelKey: 'nav.users', icon: User }] : [])
      ];
    }
    
    // Full subscription - show all nav items based on role
    // Role permissions:
    // - Admin/Super Admin: ALL modules
    // - Dispatcher: NO Accounting, Users, Compliance, System, Terminal Integration
    // - Accounting: Dashboard, Messaging, Accounting only
    // - Driver: Driver View only (separate app)
    
    const role = user?.role;
    const isAdmin = role === 'admin' || role === 'super_admin';
    // "Op" (operator) is a dispatcher-equivalent role per backend canEdit
    // logic. Treat it as a dispatcher for navigation visibility too, otherwise
    // op users see an empty sidebar AND get bounced to /unauthorized.
    const isDispatcher = role === 'dispatcher' || role === 'op';
    const isAccounting = role === 'accounting';
    const isDriver = role === 'driver';
    const isMechanic = role === 'mechanic';

    // Mechanic gets a dedicated Workbench + the Equipment Issues report view
    if (isMechanic) {
      return [
        { path: '/mechanic', labelKey: 'nav.mechanicWorkbench', icon: Wrench },
        { path: '/equipment-issues', labelKey: 'nav.equipmentIssues', icon: Wrench },
      ];
    }

    // iter 304.18 — Moving-industry tenants ALWAYS get exactly these 4 nav
    // entries (Moving, Messages, GPS, Users) by default — plus any
    // optional items the super admin has enabled per-company via
    // `user.nav_extras`. Super admins are excluded so they can still see
    // the full sidebar when impersonating any tenant.
    const isMovingTenant =
      role !== 'super_admin' &&
      (user?.industry_type === 'moving' || user?.company_category === 'moving_company');
    if (isMovingTenant) {
      const extras = new Set(Array.isArray(user?.nav_extras) ? user.nav_extras : []);
      return [
        ...((isAdmin || isDispatcher) ? [{ path: '/moving', labelKey: 'nav.moving', label: 'Moving', icon: Truck }] : []),
        ...(hasFeature('messaging') && (isAdmin || isDispatcher || isAccounting) ? [{ path: '/messages', labelKey: 'nav.messages', icon: MessageSquareText }] : []),
        ...(hasFeature('tracking') && (isAdmin || isDispatcher) ? [{ path: '/track-drivers', labelKey: 'nav.gps', icon: MapPin }] : []),
        ...(hasFeature('users') && isAdmin ? [{ path: '/users', labelKey: 'nav.users', icon: User }] : []),
        // iter 304.19 — optional per-tenant extras layered on top.
        ...(extras.has('dashboard') && (isAdmin || isDispatcher || isAccounting) ? [{ path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard }] : []),
        ...(extras.has('accounting') && (isAdmin || isAccounting) ? [{ path: '/accounting', labelKey: 'nav.accounting', icon: Calculator }] : []),
        ...(extras.has('gate_schedules') && (isAdmin || isDispatcher) ? [{ path: '/gate-schedules', labelKey: 'nav.gateSchedules', icon: Ship }] : []),
        ...(extras.has('dnd_disputes') && (isAdmin || isDispatcher) ? [{ path: '/dnd-disputes', label: 'D&D Disputes', icon: Gavel }] : []),
        ...(extras.has('broker_tms') && (isAdmin || isDispatcher) ? [{ path: '/broker', labelKey: 'nav.brokerTms', icon: Building2 }] : []),
        ...(extras.has('otr') && (isAdmin || isDispatcher) ? [{ path: '/otr/load-board', labelKey: 'nav.loadBoard', icon: Route }] : []),
        ...(extras.has('compliance') && isAdmin ? [{ path: '/compliance', labelKey: 'nav.compliance', icon: Shield }] : []),
        ...(extras.has('equipment_issues') && (isAdmin || isDispatcher) ? [{ path: '/equipment-issues', labelKey: 'nav.equipmentIssues', icon: Wrench }] : []),
        ...(extras.has('proof_of_delivery') && (isAdmin || isDispatcher || isDriver) ? [{ path: '/pod', labelKey: 'nav.proofOfDelivery', icon: ClipboardCheck }] : []),
        ...(extras.has('terminal_integration') && isAdmin ? [{ path: '/terminal-integration', labelKey: 'nav.terminalIntegration', icon: Link2 }] : []),
      ];
    }

    // iter 304.22 — Drayage tenants: purpose-built starter nav focused
    // on container life-cycle. Base = Dashboard · Messages · GPS · Gate
    // Schedules · D&D Disputes · Terminal Integration · Users. Super
    // admin can still layer `nav_extras` on top per-tenant.
    const isDrayageTenant = role !== 'super_admin' && user?.industry_type === 'drayage';
    if (isDrayageTenant) {
      const extras = new Set(Array.isArray(user?.nav_extras) ? user.nav_extras : []);
      return [
        ...(hasFeature('dashboard') && (isAdmin || isDispatcher || isAccounting) ? [{ path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard }] : []),
        ...(hasFeature('messaging') && (isAdmin || isDispatcher || isAccounting) ? [{ path: '/messages', labelKey: 'nav.messages', icon: MessageSquareText }] : []),
        ...(hasFeature('tracking') && (isAdmin || isDispatcher) ? [{ path: '/track-drivers', labelKey: 'nav.gps', icon: MapPin }] : []),
        ...(hasFeature('gate_schedules') && (isAdmin || isDispatcher) ? [{ path: '/gate-schedules', labelKey: 'nav.gateSchedules', icon: Ship }] : []),
        ...(hasFeature('dnd_disputes') && (isAdmin || isDispatcher) ? [{ path: '/dnd-disputes', label: 'D&D Disputes', icon: Gavel }] : []),
        ...(hasFeature('terminal_integration') && isAdmin ? [{ path: '/terminal-integration', labelKey: 'nav.terminalIntegration', icon: Link2 }] : []),
        ...(hasFeature('accounting') && (isAdmin || isAccounting) ? [{ path: '/accounting', labelKey: 'nav.accounting', icon: Calculator }] : []),
        ...(hasFeature('users') && isAdmin ? [{ path: '/users', labelKey: 'nav.users', icon: User }] : []),
        // extras
        ...(extras.has('otr') && (isAdmin || isDispatcher) ? [{ path: '/otr/load-board', labelKey: 'nav.loadBoard', icon: Route }] : []),
        ...(extras.has('broker_tms') && (isAdmin || isDispatcher) ? [{ path: '/broker', labelKey: 'nav.brokerTms', icon: Building2 }] : []),
        ...(extras.has('compliance') && isAdmin ? [{ path: '/compliance', labelKey: 'nav.compliance', icon: Shield }] : []),
        ...(extras.has('proof_of_delivery') && (isAdmin || isDispatcher || isDriver) ? [{ path: '/pod', labelKey: 'nav.proofOfDelivery', icon: ClipboardCheck }] : []),
        ...(extras.has('equipment_issues') && (isAdmin || isDispatcher) ? [{ path: '/equipment-issues', labelKey: 'nav.equipmentIssues', icon: Wrench }] : []),
      ];
    }

    // iter 304.22 — Logistics tenants (freight brokers / OTR-heavy):
    // load-board and broker-first nav. Base = Dashboard · Messages · GPS
    // · OTR · Broker TMS · Accounting · Users. Extras layered per-tenant.
    const isLogisticsTenant = role !== 'super_admin' && user?.industry_type === 'logistics';
    if (isLogisticsTenant) {
      const extras = new Set(Array.isArray(user?.nav_extras) ? user.nav_extras : []);
      return [
        ...(hasFeature('dashboard') && (isAdmin || isDispatcher || isAccounting) ? [{ path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard }] : []),
        ...(hasFeature('messaging') && (isAdmin || isDispatcher || isAccounting) ? [{ path: '/messages', labelKey: 'nav.messages', icon: MessageSquareText }] : []),
        ...(hasFeature('tracking') && (isAdmin || isDispatcher) ? [{ path: '/track-drivers', labelKey: 'nav.gps', icon: MapPin }] : []),
        ...(hasFeature('otr') && (isAdmin || isDispatcher) ? [{ path: '/otr/load-board', labelKey: 'nav.loadBoard', icon: Route }] : []),
        ...(hasFeature('broker_tms') && (isAdmin || isDispatcher) ? [{ path: '/broker', labelKey: 'nav.brokerTms', icon: Building2 }] : []),
        ...(hasFeature('accounting') && (isAdmin || isAccounting) ? [{ path: '/accounting', labelKey: 'nav.accounting', icon: Calculator }] : []),
        ...(hasFeature('users') && isAdmin ? [{ path: '/users', labelKey: 'nav.users', icon: User }] : []),
        // extras
        ...(extras.has('gate_schedules') && (isAdmin || isDispatcher) ? [{ path: '/gate-schedules', labelKey: 'nav.gateSchedules', icon: Ship }] : []),
        ...(extras.has('dnd_disputes') && (isAdmin || isDispatcher) ? [{ path: '/dnd-disputes', label: 'D&D Disputes', icon: Gavel }] : []),
        ...(extras.has('compliance') && isAdmin ? [{ path: '/compliance', labelKey: 'nav.compliance', icon: Shield }] : []),
        ...(extras.has('proof_of_delivery') && (isAdmin || isDispatcher || isDriver) ? [{ path: '/pod', labelKey: 'nav.proofOfDelivery', icon: ClipboardCheck }] : []),
        ...(extras.has('equipment_issues') && (isAdmin || isDispatcher) ? [{ path: '/equipment-issues', labelKey: 'nav.equipmentIssues', icon: Wrench }] : []),
      ];
    }

    return [
      // Super admin gets link to their admin panel
      ...(role === 'super_admin' ? [{ path: '/super-admin', labelKey: 'nav.superAdmin', icon: LayoutDashboard }] : []),
      
      // Dashboard - Admin, Dispatcher, Accounting
      ...(hasFeature('dashboard') && (isAdmin || isDispatcher || isAccounting) ? [{ path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard }] : []),
      
      // Messaging - Admin, Dispatcher, Accounting
      ...(hasFeature('messaging') && (isAdmin || isDispatcher || isAccounting) ? [{ path: '/messages', labelKey: 'nav.messages', icon: MessageSquareText }] : []),
      
      // GPS Tracking - Admin, Dispatcher only (NOT Accounting)
      ...(hasFeature('tracking') && (isAdmin || isDispatcher) ? [{ path: '/track-drivers', labelKey: 'nav.gps', icon: MapPin }] : []),
      
      // OTR Module - Admin, Dispatcher only (NOT Accounting)
      ...(hasFeature('otr') && (isAdmin || isDispatcher) ? [
        { path: '/otr/load-board', labelKey: 'nav.loadBoard', icon: Route },
        { path: '/otr/hos', labelKey: 'nav.hosTracking', icon: Clock }
      ] : []),
      
      // Gate Schedules - Admin, Dispatcher only (NOT Accounting)
      ...(hasFeature('gate_schedules') && (isAdmin || isDispatcher) ? [
        { path: '/gate-schedules', labelKey: 'nav.gateSchedules', icon: Ship }
      ] : []),

      // D&D Dispute Dashboard — Admin, Dispatcher (iter 257)
      // iter 304.14 — Now module-gated so super admin can grant/revoke.
      ...(hasFeature('dnd_disputes') && (isAdmin || isDispatcher) ? [
        { path: '/dnd-disputes', label: 'D&D Disputes', icon: Gavel }
      ] : []),
      
      // iter 294: Moving Dashboard — top-level page for moving-company
      // tenants (parallel to /broker for broker tenants). Nav-visible when
      // the company category is `moving_company` OR when the moving module
      // is explicitly enabled via subscription.
      // iter 294.5 — Moving is a STANDALONE module and must NOT sit next to
      // Import/Export/Local nav items. Only moving-company tenants keep it
      // in the main sidebar (as their primary landing). Super admins access
      // it via the profile dropdown / Super Admin console. All other
      // tenants who happen to have the `moving` feature flag can reach it
      // through the /moving URL directly or via the profile dropdown.
      // iter 294.6 — Closings sits directly under Moving for moving-company
      // tenants (they run both flows in parallel).
      // iter 294.10d — Single "Moving" sidebar entry only. Closings and
      // Warehouse now live as tabs INSIDE the Moving module (see
      // pages/moving/MovingLayout.js). This stops the sidebar from carrying
      // 3 siblings and keeps every moving-related workflow one click away
      // from every other.
      ...(((role === 'super_admin') || user?.industry_type === 'moving' || user?.company_category === 'moving_company' || hasFeature('moving')) && (isAdmin || isDispatcher) ? [
        { path: '/moving', labelKey: 'nav.moving', label: 'Moving', icon: Truck },
      ] : []),

      // Broker TMS - Admin, Dispatcher only (NOT Accounting)
      // iter 294.4 — super_admin sees Broker unconditionally so they can
      // manage any tenant's broker module via X-Company-Id.
      ...((role === 'super_admin' || hasFeature('broker_tms')) && (isAdmin || isDispatcher) ? [
        { path: '/broker', labelKey: 'nav.brokerTms', icon: Building2 }
      ] : []),
      
      // AI Tools - FreightMargin AI - Admin, Dispatcher, Owner Operator
      ...(hasFeature('freight_margin_ai') && (isAdmin || isDispatcher) ? [
        { path: '/ai/freight-margin', labelKey: 'nav.freightMarginAI', icon: Brain }
      ] : []),
      
      // Proof of Delivery - Admin, Dispatcher, Driver
      ...(hasFeature('pod') && (isAdmin || isDispatcher || isDriver) ? [
        { path: '/pod', labelKey: 'nav.proofOfDelivery', icon: ClipboardCheck }
      ] : []),
      
      // Accounting - Admin and Accounting only (NOT Dispatcher)
      // iter 294.4 — super_admin sees Accounting unconditionally.
      ...((role === 'super_admin' || hasFeature('accounting')) && (isAdmin || isAccounting)
        ? [{ path: '/accounting', labelKey: 'nav.accounting', icon: Calculator }] : []),
      
      // Equipment Compliance - Admin only
      ...(hasFeature('compliance') && isAdmin ? [{ path: '/compliance', labelKey: 'nav.compliance', icon: Shield }] : []),
      
      // Equipment Issues — Admin, Dispatcher
      // iter 304.14 — Module-gated via compliance module.
      ...(hasFeature('equipment_issues') && (isAdmin || isDispatcher) ? [{ path: '/equipment-issues', labelKey: 'nav.equipmentIssues', icon: Wrench }] : []),
      
      // Users - Admin only
      ...(hasFeature('users') && isAdmin ? [{ path: '/users', labelKey: 'nav.users', icon: User }] : []),
      
      // Terminal Integration - Admin only
      ...(hasFeature('terminal_integration') && isAdmin ? [{ path: '/terminal-integration', labelKey: 'nav.terminalIntegration', icon: Link2 }] : []),
      
      // System Settings - Admin only
      ...(hasFeature('system') && isAdmin ? [{ path: '/system', labelKey: 'nav.system', icon: Settings }] : [])
    ];
  };

  const navItems = getNavItems();

  // Super admin sees their dashboard as home
  // Dispatchers on mobile see messages as home for real-time driver communication
  // iter 304.17 — Moving-industry tenants land on /moving, not the container
  // dispatch Imports view. Clicking the company name / logo respects this.
  const isMobileDevice = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isMovingTenant = user?.industry_type === 'moving' || user?.company_category === 'moving_company';
  const homeLink = user?.role === 'super_admin'
    ? '/super-admin'
    : (user?.role === 'foreman')
      ? '/moving/closings/mobile'
      : (isMovingTenant && (user?.role === 'admin' || user?.role === 'dispatcher' || user?.role === 'accounting'))
        ? '/moving'
        : (user?.role === 'dispatcher' && isMobileDevice)
          ? '/messages'
          : '/dashboard';

  const isActive = (path) => location.pathname === path;

  // iter 265.3 — Gmail-style `g h` shortcut fires the same reset-navigate
  // that clicking the Drayly logo does.
  useGoHomeShortcut(homeLink);

  // Handle test mode role switch
  const handleTestModeSwitch = (role) => {
    setTestAsRole(role);
    // Navigate to appropriate page
    if (role === 'driver') {
      navigate('/driver');
    } else if (role === 'admin') {
      navigate('/dashboard');
    } else if (role === null) {
      navigate('/super-admin');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Test Mode Banner - Only for Super Admin */}
      {isSuperAdmin && (
        <div className={`fixed top-0 left-0 right-0 z-[60] ${isInTestMode || isInTierTestMode ? 'bg-orange-600' : 'bg-purple-600'} text-white px-4 py-1.5`}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FlaskConical className="h-4 w-4" />
              <span className="text-sm font-medium">
                {isInTestMode 
                  ? `${t('superAdmin.testMode')}: ${testMode?.toUpperCase()}` 
                  : t('nav.superAdmin')}
                {isInTierTestMode && (
                  <span className="ml-2 px-2 py-0.5 bg-white/20 rounded text-xs">
                    {SUBSCRIPTION_TIERS[testTier]?.name}
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {(isInTestMode || isInTierTestMode) ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    handleTestModeSwitch(null);
                    setTestSubscriptionTier(null);
                    navigate('/super-admin');
                  }}
                  className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white"
                >
                  <X className="h-3 w-3 mr-1" />
                  {t('superAdmin.exitTestMode')}
                </Button>
              ) : (
                <>
                  <span className="text-xs opacity-80 mr-2">{t('superAdmin.testAs')}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleTestModeSwitch('admin')}
                    className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    {t('superAdmin.admin')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleTestModeSwitch('driver')}
                    className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white"
                  >
                    <Truck className="h-3 w-3 mr-1" />
                    {t('superAdmin.driver')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* iter 294.5 — Cross-Tenant Warning Banner. Persistent amber ribbon
          shown ONLY when a super admin has explicitly picked a tenant so
          any write action (invoice create, moving job save, shipment edit,
          credential test, …) is visually attributed to the target tenant.
          Prevents "wait, was that write mine or the tenant's?" moments. */}
      {isSuperAdmin && superAdminActingAs && (
        <div
          className={`fixed left-0 right-0 z-[55] bg-amber-500 text-slate-900 px-4 py-1.5 shadow-md ${
            (isInTestMode || isInTierTestMode) ? 'top-10' : 'top-0'
          }`}
          data-testid="cross-tenant-warning-banner"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs sm:text-sm font-medium">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                You are acting as{' '}
                <span className="font-bold underline">
                  {superAdminActingAsName || superAdminActingAs}
                </span>
                {' '}— every write on this session will land in their tenant data.
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs bg-slate-900/10 hover:bg-slate-900/20 text-slate-900 font-semibold"
              onClick={() => setSuperAdminActingAs('', '')}
              data-testid="clear-tenant-scope-btn"
            >
              <X className="h-3 w-3 mr-1" />
              Exit tenant view
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`h-16 bg-slate-900 border-b border-slate-800 fixed left-0 right-0 z-[1000] ${
        // iter 294.5 — Stack top-offset for banners. Test-mode banner (h-10)
        // + tenant-scope banner (h-8) each shift the header down.
        isInTestMode || isInTierTestMode
          ? (isSuperAdmin && superAdminActingAs ? 'top-[72px]' : 'top-10')
          : (isSuperAdmin && superAdminActingAs ? 'top-8' : 'top-0')
      }`}>
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left: Logo & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-slate-400"
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Link
              to={homeLink}
              onClick={(e) => {
                // iter 265.2 — Clicking the Drayly logo should behave like a
                // "reset view" action: if the user is already on the home
                // route, React Router's Link would be a no-op and any active
                // filters / selected containers would persist. We broadcast a
                // navigation state (`reset` timestamp) so dashboards can
                // listen and clear their local filter state.
                e.preventDefault();
                navigate(homeLink, { state: { reset: Date.now() } });
              }}
              className="flex items-center gap-3"
              data-testid="drayly-logo-home"
            >
              {companyData?.logo_url ? (
                <div className="w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden bg-slate-800">
                  <img 
                    src={companyData.logo_url} 
                    alt={companyData.name || 'Company Logo'} 
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden bg-slate-900">
                  <img src="/logo192.png" alt="Drayly" className="w-full h-full object-contain" />
                </div>
              )}
              <span className="font-heading font-bold text-xl text-white hidden sm:block">
                {companyData?.name || 'Drayly'}
              </span>
            </Link>
          </div>

          {/* Center: Nav (Desktop) - Primary items + More dropdown for overflow */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {/* Show first 6 nav items directly */}
            {navItems.slice(0, 6).map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm transition-colors whitespace-nowrap text-sm ${
                  isActive(item.path)
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <div className="relative">
                  <item.icon className="h-4 w-4" />
                  {item.path === '/compliance' && complianceAlerts.has_critical && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                  )}
                </div>
                <span className="hidden xl:inline">{item.label || t(item.labelKey, item.labelKey)}</span>
                {item.path === '/compliance' && complianceAlerts.overdue > 0 && (
                  <Badge className="ml-1 bg-red-500/20 text-red-400 border-red-500/30 text-[10px] px-1 py-0">
                    {complianceAlerts.overdue}
                  </Badge>
                )}
                {item.path === '/messages' && unreadMessageCount > 0 && (
                  <Badge className="ml-1 bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px] px-1 py-0" data-testid="unread-messages-badge">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </Badge>
                )}
              </Link>
            ))}
            
            {/* More dropdown for remaining items */}
            {navItems.length > 6 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`flex items-center gap-1 px-2.5 py-1.5 text-sm ${
                      navItems.slice(6).some(item => isActive(item.path))
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                    data-testid="nav-more-btn"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="hidden xl:inline">{t('nav.more')}</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 bg-slate-900 border-slate-800">
                  {navItems.slice(6).map(item => (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-2 cursor-pointer ${
                        isActive(item.path)
                          ? 'bg-blue-600/20 text-blue-400'
                          : 'text-slate-300 hover:text-white'
                      }`}
                    >
                      <div className="relative">
                        <item.icon className="h-4 w-4" />
                        {item.path === '/compliance' && complianceAlerts.has_critical && (
                          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                        )}
                      </div>
                      <span>{item.label || t(item.labelKey, item.labelKey)}</span>
                      {item.path === '/compliance' && complianceAlerts.overdue > 0 && (
                        <Badge className="ml-auto bg-red-500/20 text-red-400 border-red-500/30 text-[10px] px-1 py-0">
                          {complianceAlerts.overdue}
                        </Badge>
                      )}
                      {item.path === '/messages' && unreadMessageCount > 0 && (
                        <Badge className="ml-auto bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px] px-1 py-0">
                          {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                        </Badge>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          {/* Right: Language, Theme Toggle, Notifications & Profile */}
          <div className="flex items-center gap-2">
            {/* Beta Badge */}
            <BetaBadge />
            
            {/* Bug Report Button */}
            <BugReportButton />

            {/* iter 294.4 — Global Tenant Picker for super admins. Injects
                X-Company-Id on every non-super-admin API call so Broker,
                Accounting, Moving, Terminal Integration, etc. all become
                usable without impersonating an owner. Choice persisted in
                localStorage.super_admin_acting_as. */}
            {isSuperAdmin && (
              <TenantScopePicker
                api={api}
                value={superAdminActingAs}
                onChange={setSuperAdminActingAs}
              />
            )}

            {/* Language Selector */}
            <LanguageSelector onLanguageChange={handleLanguageChange} compact />

            {/* Online Users Indicator */}
            <OnlineUsersIndicator />

            {/* Compact tables */}
            <CompactTablesToggle />

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-slate-400 hover:text-white"
              data-testid="theme-toggle-btn"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {/* Notifications */}
            <DropdownMenu open={bellOpen} onOpenChange={(open) => { setBellOpen(open); if (open) setBellPulse(false); }}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`relative text-slate-400 hover:text-white ${bellPulse ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-950 rounded-full animate-pulse' : ''}`}
                  data-testid="notifications-btn"
                >
                  <Bell className={`h-5 w-5 ${bellPulse ? 'text-amber-300' : ''}`} />
                  {bellPulse && (
                    <span className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping pointer-events-none" />
                  )}
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-slate-900 border-slate-800">
                <div className="p-3 border-b border-slate-800 flex items-center justify-between">
                  <span className="font-heading font-semibold text-white">{t('notifications.title')}</span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.preventDefault(); setShowMyTags((v) => !v); }}
                      className={`text-xs h-7 px-2 ${showMyTags ? 'text-amber-300 bg-amber-500/10' : 'text-slate-400 hover:text-white'}`}
                      data-testid="my-tags-toggle"
                    >
                      My tags
                    </Button>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleMarkAllRead}
                        className="text-blue-400 hover:text-blue-300 text-xs h-7 px-2"
                      >
                        {t('notifications.markAllRead')}
                      </Button>
                    )}
                    {notifications.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearAllNotifications}
                        className="text-red-400 hover:text-red-300 text-xs h-7 px-2"
                        data-testid="clear-all-notifications"
                      >
                        {t('notifications.clear')}
                      </Button>
                    )}
                  </div>
                </div>
                <ScrollArea className="max-h-[300px]">
                  {showMyTags ? (
                    <MyTagsList api={api} onOpen={(jobId) => { setBellOpen(false); navigate(`/moving?comments=${jobId}`); }} />
                  ) : notifications.length === 0 ? (
                    <p className="p-4 text-center text-slate-500 text-sm">{t('notifications.noNotifications')}</p>
                  ) : (
                    notifications.slice(0, 10).map(notif => (
                      <div
                        key={notif.id}
                        className={`p-3 border-b border-slate-800 cursor-pointer hover:bg-slate-800 ${
                          !notif.read ? 'bg-blue-900/20 border-l-2 border-l-blue-500' : ''
                        }`}
                        onClick={() => {
                          if (notif.link) {
                            navigate(notif.link);
                          } else if (notif.type === 'moving_bulk_upload' && notif.related_id) {
                            navigate(`/moving?batch=${notif.related_id}`);
                          }
                          if (notif.link || notif.type === 'moving_bulk_upload') setBellOpen(false);
                          if (!notif.read) {
                            // Optimistic update - mark as read locally immediately
                            setNotifications(prev => prev.map(n => 
                              n.id === notif.id ? { ...n, read: true } : n
                            ));
                            setUnreadCount(prev => Math.max(0, prev - 1));
                            
                            // Sync with server
                            api.put(`/notifications/${notif.id}/read`).catch(() => {
                              // Revert on error
                              fetchNotifications();
                            });
                          }
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${
                            notif.type === 'time_off_approved' ? 'bg-green-500' :
                            notif.type === 'time_off_denied' ? 'bg-red-500' :
                            'bg-blue-500'
                          }`}></div>
                          <div className="flex-1">
                            <p className="text-white text-sm font-medium">{notif.title}</p>
                            <p className="text-slate-400 text-xs mt-1">{notif.message}</p>
                            <p className="text-slate-500 text-xs mt-1">
                              {notif.created_at && new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </ScrollArea>
                {notifications.length > 10 && (
                  <div className="p-2 border-t border-slate-800 text-center">
                    <p className="text-xs text-slate-500">
                      {t('notifications.showingOf', { count: 10, total: notifications.length })}
                    </p>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Role badge — always visible in the header so users know
                their access level at a glance. Sits OUTSIDE the profile
                dropdown so we don't nest interactive elements. */}
            <RoleBadge role={user?.role} />

            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 text-slate-300 hover:text-white"
                  data-testid="profile-menu-btn"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-blue-600 text-white text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block">{user?.name}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
                <div className="p-3 border-b border-slate-800">
                  <p className="text-white font-medium">{user?.name}</p>
                  <p className="text-slate-400 text-xs">{user?.email}</p>
                  <Badge className="mt-2 bg-blue-600/20 text-blue-400 border-blue-600/30">
                    {ROLE_LABELS[user?.role] || user?.role}
                  </Badge>
                </div>
                <DropdownMenuItem 
                  onClick={() => navigate('/system?tab=profile')}
                  className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                  data-testid="profile-link"
                >
                  <User className="h-4 w-4 mr-2" />
                  {t('nav.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/system')}
                  className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                  data-testid="settings-link"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t('nav.settings')}
                </DropdownMenuItem>
                {/* Marketplace link in dropdown */}
                <DropdownMenuItem 
                  onClick={() => navigate('/marketplace')}
                  className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                  data-testid="marketplace-link"
                >
                  <Store className="h-4 w-4 mr-2" />
                  {t('nav.marketplace')}
                </DropdownMenuItem>
                {/* Community Hub link */}
                <DropdownMenuItem 
                  onClick={() => navigate('/marketplace/community')}
                  className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                  data-testid="community-link"
                >
                  <MessageSquareText className="h-4 w-4 mr-2" />
                  {t('nav.communityHub')}
                </DropdownMenuItem>
                {/* Roadside Rescue - available for all users */}
                <DropdownMenuItem 
                  onClick={() => navigate('/roadside')}
                  className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                  data-testid="roadside-rescue-link"
                >
                  <Car className="h-4 w-4 mr-2" />
                  {t('nav.roadsideRescue')}
                </DropdownMenuItem>
                {/* iter 294.10 — Moving Jobs moved OUT of the profile dropdown.
                    It now lives in the main sidebar (for moving-company tenants)
                    or in the "More" overflow dropdown for others. Keeping the
                    profile dropdown clean of module links. */}
                {/* Owner Operator link - only for users registered as owner operators */}
                {isOwnerOperator && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/owner-operator')}
                    className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                    data-testid="owner-operator-link"
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    {t('nav.ownerOperator')}
                  </DropdownMenuItem>
                )}
                {/* Admin Requests link - only for admin/dispatcher */}
                {(user?.role === 'admin' || user?.role === 'dispatcher') && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/admin-requests')}
                    className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                    data-testid="admin-requests-link"
                  >
                    <FileQuestion className="h-4 w-4 mr-2" />
                    {t('nav.requestChanges')}
                  </DropdownMenuItem>
                )}
                {/* Data Migration link - only for admin */}
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/data-migration')}
                    className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                    data-testid="data-migration-link"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t('nav.dataMigration')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem
                  onClick={() => {
                    // Trigger PWA install prompt
                    const evt = new CustomEvent('trigger-pwa-install');
                    window.dispatchEvent(evt);
                  }}
                  className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                  data-testid="install-app-link"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('nav.installApp')}
                </DropdownMenuItem>
                {/* iter 264b — Test push notification. Only visible when
                    permission has been granted so we don't offer a button
                    that can't succeed. */}
                {typeof Notification !== 'undefined' && Notification.permission === 'granted' && (
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const r = await api.post('/notifications/test-push');
                        if (r.data?.success) {
                          toast.success('Test notification sent! Check your device.');
                        } else {
                          toast.error(
                            r.data?.details?.reason === 'No active subscriptions'
                              ? 'No active subscription yet — try again in a few seconds.'
                              : 'Could not send test notification.'
                          );
                        }
                      } catch (e) {
                        toast.error('Test notification failed.');
                      }
                    }}
                    className="text-slate-300 hover:bg-slate-800 cursor-pointer"
                    data-testid="test-notification-link"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Test notification
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-400 hover:bg-slate-800 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
          {/* iter 303 — Use 100dvh + flex column so the nav always scrolls to
              its last item, even when the mobile address-bar collapses (which
              iOS/Android don't reflect in vh/inset-0 anchors). Safe-area
              padding-bottom keeps the last row above notched-phone home
              indicators. */}
          <div
            className="absolute left-0 top-0 w-64 bg-slate-900 border-r border-slate-800 flex flex-col"
            style={{
              height: '100dvh',
              paddingTop: isSuperAdmin ? '6.5rem' : '4rem',
            }}
            data-testid="mobile-sidebar"
          >
            <nav
              className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-1"
              style={{
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
              }}
              data-testid="mobile-sidebar-nav"
            >
              {/* iter 304 — Super Admin tenant picker inside the mobile
                  drawer. The top-bar picker is desktop-only, so on phones
                  the previous "pick a tenant" hint was unreachable. */}
              {isSuperAdmin && (
                <TenantScopePicker
                  api={api}
                  value={superAdminActingAs}
                  onChange={(id, name) => { setSuperAdminActingAs(id, name); setSidebarOpen(false); }}
                  variant="mobile"
                />
              )}
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <div className="relative">
                    <item.icon className="h-5 w-5" />
                    {item.path === '/compliance' && complianceAlerts.has_critical && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    )}
                  </div>
                  {item.label || t(item.labelKey, item.labelKey)}
                  {item.path === '/compliance' && complianceAlerts.overdue > 0 && (
                    <Badge className="ml-auto bg-red-500/20 text-red-400 border-red-500/30 text-xs px-1.5">
                      {complianceAlerts.overdue}
                    </Badge>
                  )}
                  {item.path === '/messages' && unreadMessageCount > 0 && (
                    <Badge className="ml-auto bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs px-1.5" data-testid="unread-messages-badge-mobile">
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </Badge>
                  )}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`min-h-screen ${isSuperAdmin ? 'pt-26' : 'pt-16'}`} style={{ paddingTop: isSuperAdmin ? '6.5rem' : '4rem' }}>
        {/* iter 264b — Push notification opt-in banner. Only renders when
            permission is 'default' and user hasn't dismissed for the session.
            Only shown to admins/dispatchers/drivers who receive real-time
            dispatch alerts (accounting/mechanic don't need it). */}
        {['admin', 'super_admin', 'dispatcher', 'op', 'driver'].includes(user?.role) && (
          <PushNotificationBanner appType={user?.role === 'driver' ? 'driver' : 'dispatch'} />
        )}
        {/* Renewal Reminder for accounting users */}
        {(user?.role === 'accounting' || user?.role === 'admin') && (
          <div className={`fixed left-1/2 transform -translate-x-1/2 z-30 w-full max-w-xl px-4 ${isSuperAdmin ? 'top-30' : 'top-20'}`} style={{ top: isSuperAdmin ? '7.5rem' : '5rem' }}>
            <RenewalReminder />
          </div>
        )}
        {/* Clock Widget for users with time tracking (full sub or timeclock standalone) */}
        {hasFeature('clock') && (
          <div className="fixed bottom-4 right-4 z-30 w-80">
            <ClockWidget />
          </div>
        )}
        <OverdueBanner />
        {children}
        
        {/* AI Assistant - Available for admin roles */}
        {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'dispatcher') && (
          <AIAssistant />
        )}
      </main>
      
      {/* Beta Disclaimer Modal - shows once per version */}
      <BetaDisclaimerModal />

      {/* iter 304.14 — First-run 3-step wizard for Moving-industry admins.
          Renders itself only when the tenant is a moving company and the
          user hasn't dismissed / completed it. */}
      <MovingOnboardingWizard />
      
      {/* PWA Install Prompt for Dispatchers */}
      <PWAInstallBanner appType="dispatch" />
    </div>
  );
};

export default MainLayout;

// iter 294.4 — Global Tenant Picker component for super admins.
// Fetches the tenant list once, renders a compact amber-outlined Select
// in the top bar, and calls setSuperAdminActingAs on change. Kept in the
// same file so the layout stays a single import for the rest of the app.
const TenantScopePicker = ({ api, value, onChange, variant = 'desktop' }) => {
  const [options, setOptions] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/super-admin/companies');
        if (cancelled) return;
        const list = (data?.companies || data || []).map((c) => ({
          id: c.company_id || c.id,
          name: c.name || c.company_name || c.company_id,
        })).filter((x) => x.id);
        setOptions(list);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('tenant picker: failed to load /super-admin/companies', e);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const handleChange = (v) => {
    if (v === '__all__') { onChange('', ''); return; }
    const opt = options.find((o) => o.id === v);
    onChange(v, opt?.name || '');
  };

  // iter 304 — Mobile variant is a full-width block used inside the mobile
  // sidebar drawer so super admins can pick a tenant without a desktop-only
  // top bar. Previously the picker was `hidden md:flex`, which is why the
  // "pick a tenant" hint on mobile was a dead end.
  if (variant === 'mobile') {
    return (
      <div
        className="mb-3 rounded-md border border-amber-600 bg-amber-500/10 p-2"
        data-testid="super-admin-tenant-picker-mobile"
      >
        <div className="text-[10px] uppercase tracking-wide text-amber-300 font-semibold mb-1">
          Tenant scope (Super Admin)
        </div>
        <Select value={value || '__all__'} onValueChange={handleChange}>
          <SelectTrigger
            className="h-10 w-full border-amber-600 bg-slate-950 text-amber-100 text-sm"
            data-testid="tenant-scope-select-mobile"
          >
            <SelectValue placeholder="All tenants" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value="__all__">All tenants (cross-tenant view)</SelectItem>
            {!loaded && (
              <SelectItem value="__loading__" disabled>Loading…</SelectItem>
            )}
            {options.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className="hidden md:flex items-center gap-1" data-testid="super-admin-tenant-picker-global">
      <span className="text-[10px] uppercase tracking-wide text-amber-300 font-semibold">Tenant</span>
      <Select value={value || '__all__'} onValueChange={handleChange}>
        <SelectTrigger
          className="h-8 w-56 border-amber-600 bg-amber-500/10 text-amber-100 text-xs"
          data-testid="tenant-scope-select"
        >
          <SelectValue placeholder="All tenants (super admin)" />
        </SelectTrigger>
        <SelectContent className="max-h-80">
          <SelectItem value="__all__">All tenants (cross-tenant view)</SelectItem>
          {!loaded && (
            <SelectItem value="__loading__" disabled>Loading…</SelectItem>
          )}
          {options.map((t) => (
            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
